import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Subject, TimeoutError } from 'rxjs';

export type NotificationType = 'success' | 'error' | 'info';

export interface Notification {
  id: string;
  type: NotificationType;
  message: string;
  /** Дополнительный текст (например список полей) */
  detail?: string;
  autoCloseMs?: number;
}

@Injectable({ providedIn: 'root' })
export class NotificationService {
  private readonly notifications$ = new Subject<Notification>();
  private idCounter = 0;

  /** Стрим уведомлений для подписки из UI */
  get notifications() {
    return this.notifications$.asObservable();
  }

  private nextId(): string {
    return `notif-${++this.idCounter}-${Date.now()}`;
  }

  private push(type: NotificationType, message: string, detail?: string, autoCloseMs?: number): void {
    const notification: Notification = {
      id: this.nextId(),
      type,
      message,
      detail,
      autoCloseMs
    };
    // Откладываем в следующий тик, чтобы в zoneless успела сработать проверка представления
    setTimeout(() => this.notifications$.next(notification), 0);
  }

  showSuccess(message: string, detail?: string, autoCloseMs = 5000): void {
    this.push('success', message, detail, autoCloseMs);
  }

  showError(message: string, detail?: string, autoCloseMs?: number): void {
    this.push('error', message, detail, autoCloseMs ?? 8000);
  }

  showInfo(message: string, detail?: string, autoCloseMs = 5000): void {
    this.push('info', message, detail, autoCloseMs);
  }

  /**
   * Показать ошибку из ответа API (в т.ч. 422 с message и missing_fields).
   * Использовать в error-колбэке subscribe: error: (err) => this.notificationService.showApiError(err, 'Не удалось выполнить действие.')
   */
  showApiError(err: unknown, fallbackMessage: string): void {
    const { message, detail } = extractApiError(err, fallbackMessage);
    this.showError(message, detail);
  }
}

function parseApiErrorBody(err: unknown): Record<string, unknown> {
  let body = (err as { error?: unknown })?.error;
  if (typeof body === 'string') {
    try {
      body = JSON.parse(body) ?? {};
    } catch {
      body = {};
    }
  }

  if (body && typeof body === 'object') {
    return body as Record<string, unknown>;
  }

  return {};
}

/** Ответ API с кодом 422/409 — объект (message + missing_fields + invalid_fields) */
export interface Api422DetailObject {
  message?: string;
  missing_fields?: string[];
  invalid_fields?: string[];
}

/** Элемент массива ошибок валидации 422 */
export interface Api422DetailItem {
  loc?: (string | number)[];
  msg?: string;
  type?: string;
}

export interface AppErrorDetailItem {
  type?: string;
  field?: string | null;
  message?: string;
  meta?: Record<string, unknown>;
}

export interface AppErrorResponseBody {
  code?: string;
  message?: string;
  details?: AppErrorDetailItem[];
  request_id?: string;
}

export type Api422Detail = Api422DetailObject | Api422DetailItem[] | undefined;

/** Человекочитаемые названия полей (в т.ч. PUT /businesses/{id}) для ошибок */
const FIELD_LABELS: Record<string, string> = {
  organization_name: 'Название организации',
  organization_description: 'Описание организации',
  sphere: 'Сфера деятельности',
  picture_url: 'Изображение',
  discount_percent: 'Процент скидки',
  discount_desc: 'Описание скидки',
  country: 'Страна',
  city: 'Город',
  address: 'Адрес',
  full_name: 'ФИО',
  phone: 'Телефон',
  email: 'Email',
  preferred_contact_method: 'Предпочитаемый способ связи',
  contact_phone: 'Контактный телефон',
  additional_phone: 'Контактный телефон',
  whatsapp_number: 'WhatsApp',
  contact_email: 'Контактный email',
  additional_email: 'Контактный email',
  offer_accepted: 'Акцепт оферты',
  telegram_username: 'Telegram',
  comment: 'Комментарий',
  company_type: 'Тип компании',
  inn: 'ИНН',
  ogrn: 'ОГРН',
  bank_number: 'Номер счёта',
  bank_id_code: 'БИК',
  bank_name: 'Название банка',
  correspondent_account: 'Корр. счёт',
  passport_series: 'Серия паспорта',
  passport_number: 'Номер паспорта',
  issued_by: 'Кем выдан',
  issued_at: 'Дата выдачи',
  issuer_code: 'Код подразделения',
  reporting_method: 'Способ отчётности по продажам',
  product_link: 'Ссылка на товар',
  map_link: 'Ссылка на организацию в картах',
  tags: 'Ключевые слова',
  promocodes_specified: 'Загруженные промокоды'
};

/**
 * Форматирует detail из ответа 422 для показа в уведомлении.
 * Поддерживает: { message, missing_fields } и массив [{ loc, msg, type }].
 */
/** @deprecated use formatApiDetailError */
export const formatApi422 = formatApiDetailError;

export function formatApiDetailError(detail: Api422Detail): { message: string; detail?: string } {
  if (!detail) return { message: 'Ошибка валидации.' };
  if (Array.isArray(detail)) {
    const messages = detail.map((d) => d?.msg).filter(Boolean);
    return {
      message: 'Ошибка валидации.',
      detail: messages.length ? messages.join(' ') : undefined
    };
  }
  const obj = detail as Api422DetailObject;
  if (!obj.message) return { message: 'Ошибка валидации.' };
  const parts: string[] = [];
  if (obj.missing_fields?.length) {
    parts.push('Не заполнены: ' + obj.missing_fields.map((f) => FIELD_LABELS[f] ?? f).join(', '));
  }
  if (obj.invalid_fields?.length) {
    parts.push('Некорректные: ' + obj.invalid_fields.map((f) => FIELD_LABELS[f] ?? f).join(', '));
  }
  return {
    message: obj.message,
    detail: parts.length ? parts.join('. ') : undefined
  };
}

export function formatAppErrorDetails(details?: AppErrorDetailItem[]): string | undefined {
  if (!Array.isArray(details) || details.length === 0) return undefined;

  const parts = details
    .map((item) => {
      const message = typeof item?.message === 'string' ? item.message.trim() : '';
      if (!message) return null;

      const field = typeof item?.field === 'string' && item.field.trim()
        ? FIELD_LABELS[item.field] ?? item.field
        : null;

      return field ? `${field}: ${message}` : message;
    })
    .filter((item): item is string => Boolean(item));

  return parts.length ? parts.join(' ') : undefined;
}

const TRACKER_ERROR_CODES: Record<string, string> = {
  'auth.password_matches_current': 'Новый пароль не должен совпадать с текущим',
  'category.used_in_active_raffle_builder': 'Категория используется в активном розыгрыше, удалить нельзя',
  'accounting.no_eligible_orders': 'Нет заказов, готовых к выставлению счёта (минимум 100 ₽ и период 14 дней)',
  // BE-855: гейты по магазину для акции.
  'shop.not_selectable': 'Этот магазин ещё нельзя выбрать: он не отправлен на модерацию.',
  'promotion.shop_required': 'Выберите магазин для акции перед отправкой на проверку.',
  'promotion.shop_not_approved': 'Магазин ещё не одобрен. Дождитесь его активации, чтобы продолжить.',
  // BE-1335: тексты бэка здесь технические и переменных не несут — свой понятнее.
  // Коды, у которых в message даты, суммы и числа (draw_window_open, prize_fund_insufficient,
  // not_enough_participants), сюда класть нельзя: extractApiError выбросит серверный текст.
  'raffle.seed_missing': 'У розыгрыша нет ключа тиража, провести его честно нельзя. Повтор не поможет — нужна помощь разработчиков.',
  'raffle.bitcoin_unavailable': 'Источник случайности временно недоступен. Это сбой сети, а не ошибка настроек — повторите позже.',
  'raffle.bitcoin_source_mismatch': 'Источники случайности разошлись в данных. Обычно проходит само — повторите позже.',
};

export function extractApiError(err: unknown, fallbackMessage: string): { message: string; detail?: string } {
  const body = parseApiErrorBody(err);
  const errorCode = typeof (body as { code?: unknown }).code === 'string' ? (body as { code: string }).code : '';
  if (errorCode && TRACKER_ERROR_CODES[errorCode]) {
    return { message: TRACKER_ERROR_CODES[errorCode] };
  }
  const detailPayload = (body as { detail?: Api422Detail | string })?.detail;
  const appError = body as AppErrorResponseBody;

  if (typeof appError.message === 'string' && appError.message.trim() && Array.isArray(appError.details)) {
    return {
      message: appError.message,
      detail: formatAppErrorDetails(appError.details)
    };
  }

  if (typeof detailPayload === 'string') {
    return { message: detailPayload };
  }

  if (detailPayload != null && typeof detailPayload === 'object' && !Array.isArray(detailPayload)) {
    const wrappedAppError = detailPayload as AppErrorResponseBody;
    if (typeof wrappedAppError.message === 'string' && wrappedAppError.message.trim() && Array.isArray(wrappedAppError.details)) {
      return {
        message: wrappedAppError.message,
        detail: formatAppErrorDetails(wrappedAppError.details)
      };
    }
  }

  if (detailPayload != null) {
    return formatApiDetailError(detailPayload);
  }

  // BE-1545: у HttpErrorResponse и TimeoutError `message` — техническая строка Angular/rxjs
  // («Http failure response for …», «Timeout has occurred»), а не текст сервера.
  const ownMessage =
    err instanceof HttpErrorResponse || err instanceof TimeoutError ? undefined : (err as { message?: unknown })?.message;
  const message =
    (body as { error?: unknown })?.error ??
    (body as { message?: unknown })?.message ??
    ownMessage;

  return {
    message: typeof message === 'string' && message.trim() ? message : fallbackMessage
  };
}

export function extractApiErrorMessage(err: unknown, fallbackMessage: string): string {
  const { message, detail } = extractApiError(err, fallbackMessage);
  return detail ? `${message} ${detail}` : message;
}

/** BE-800: отказ по отсутствию гранулярного права — в отличие от отказа по роли (403 без кода). */
export const PERMISSION_DENIED_CODE = 'auth.permission_denied';

/**
 * BE-602: достаёт `code` из AppError-ответа (новый формат {code, message, details, request_id}).
 * Поддерживает прямой и обёрнутый в `detail` варианты. Возвращает undefined, если кода нет.
 * Использовать для маршрутизации обработки 403 (например, `business.permission_denied`).
 */
export function extractApiErrorCode(err: unknown): string | undefined {
  const body = parseApiErrorBody(err);
  const direct = (body as AppErrorResponseBody).code;
  if (typeof direct === 'string' && direct.trim()) return direct;

  const wrapped = (body as { detail?: AppErrorResponseBody }).detail;
  if (wrapped && typeof wrapped === 'object' && typeof wrapped.code === 'string' && wrapped.code.trim()) {
    return wrapped.code;
  }
  return undefined;
}
